import React, { lazy, Suspense } from 'react';
import { motion } from 'framer-motion';

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-20">
    <motion.div
      className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full"
      animate={{ rotate: 360 }}
      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
    />
  </div>
);

// Higher-order component for lazy loading
export const withLazyLoading = (importFunction, fallback = <LoadingSpinner />) => {
  const LazyComponent = lazy(importFunction);
  
  return (props) => (
    <Suspense fallback={fallback}>
      <LazyComponent {...props} />
    </Suspense>
  );
};

// Optimized image component with lazy loading and performance optimizations
export const OptimizedImage = ({ 
  src, 
  alt, 
  className = '', 
  placeholder = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><rect width="1" height="1" fill="%23374151"/></svg>',
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [imageSrc, setImageSrc] = React.useState(placeholder);

  React.useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImageSrc(src);
      setIsLoaded(true);
    };
    img.src = src;
  }, [src]);

  return (
    <motion.img
      src={imageSrc}
      alt={alt}
      className={`transition-opacity duration-500 ${className} ${
        isLoaded ? 'opacity-100' : 'opacity-50'
      }`}
      initial={{ opacity: 0 }}
      animate={{ opacity: isLoaded ? 1 : 0.5 }}
      transition={{ duration: 0.5 }}
      loading="lazy"
      {...props}
    />
  );
};

// Performance monitoring hook
export const usePerformanceMonitor = () => {
  React.useEffect(() => {
    if (typeof window !== 'undefined' && window.performance) {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.entryType === 'largest-contentful-paint') {
            console.log('LCP:', entry.startTime);
          }
          if (entry.entryType === 'first-input') {
            console.log('FID:', entry.processingStart - entry.startTime);
          }
        });
      });

      observer.observe({ entryTypes: ['largest-contentful-paint', 'first-input'] });

      return () => observer.disconnect();
    }
  }, []);
};

export default {
  withLazyLoading,
  OptimizedImage,
  usePerformanceMonitor,
  LoadingSpinner
};